import type { AgentStatusMetricPayload } from "@/types/socket"
import type {
  CreateTranscriptMessageArgs,
  GetToolMediaArgs,
  ReportTranscriptAnnotationFailureArgs,
  RunGraphqlToolsArgs,
  RunToolArgs,
  VoiceAdditionalToolCall,
  VoiceChatToolCandidate,
  VoiceCutoffReason,
  VoiceGraphqlToolResult,
  VoiceHistoryItem,
  VoiceLiveEntry,
  VoiceToolMedia,
  VoiceToolResult,
  VoiceTranscriptPersistenceResult,
  VoiceTranscriptRole,
} from "@/types/voice-chat"

import type { VoiceTurnTransition } from "@/lib/voice-chat/voice-chat-utils"

export type VoiceConnectionStatus =
  | "idle"
  | "connecting"
  | "reconnecting"
  | "listening"
  | "assistant_speaking"
  | "error"

export type VoiceRealtimeMessageRole = VoiceTranscriptRole | "system"

export type VoiceResponseCreateTrigger =
  | "tool_result"
  | "phantom_interrupt_resume"
  | "pause_resume"
  | "greeting"
  | "place_annotation"

export interface VoiceResponseMetadata {
  mindtrip_trigger?: VoiceResponseCreateTrigger
  // OpenAI accepts at most 16 string pairs; keys are limited to 64 characters
  // and values to 512 characters.
  [key: string]: string | undefined
}

export interface VoiceRealtimeResponseOutputItem {
  id: string
  type: string
  role?: VoiceRealtimeMessageRole
}

export interface VoiceRealtimeResponseDiagnostic {
  providerEventId: string | null
  responseId: string | null
  status: string | null
  statusDetails: unknown
  metadata: VoiceResponseMetadata | null
  outputItems: Array<{
    id: string
    type: string
    role: VoiceRealtimeMessageRole | null
  }>
  outputItemCount: number
  inputTokens: number | null
  outputTokens: number | null
  cachedInputTokens: number | null
  textInputTokens: number | null
  audioInputTokens: number | null
}

export interface VoiceRealtimeTracingConfiguration {
  // Names the trace in the Traces Dashboard.
  workflow_name?: string
  // Groups related traces and enables dashboard filtering.
  group_id?: string
  // Arbitrary values used to filter traces in the dashboard.
  metadata?: Record<string, unknown>
}

// null disables tracing; "auto" uses provider defaults. Once enabled, tracing
// cannot change for the session.
export type VoiceRealtimeTracing =
  | null
  | "auto"
  | VoiceRealtimeTracingConfiguration

export type VoiceRealtimeResponseInputItem =
  | {
      // References an existing item from the session conversation by id.
      type: "item_reference"
      id: string
    }
  | {
      type: "message"
      role: "system" | "user"
      content: Array<{
        type: "input_text"
        text: string
      }>
    }

export interface VoiceRealtimeFunctionTool {
  type: "function"
  name: string
  description: string
  parameters: Record<string, unknown>
}

export interface VoiceRealtimeFunctionToolChoice {
  type: "function"
  name: string
}

export interface VoiceRealtimeResponseCreateParams {
  // "none" prevents insertion into the default conversation. Persist an
  // authoritative result in application storage if it must survive disconnect
  // or session loss.
  conversation?: "auto" | "none"
  // Replaces the default conversation context for this response; [] supplies
  // no prior context, while items can reference session ids or add text messages.
  input?: VoiceRealtimeResponseInputItem[]
  // Overrides the session instructions for this response.
  instructions?: string
  // Response-scoped tools are used by hidden out-of-band extraction responses.
  tools?: VoiceRealtimeFunctionTool[]
  tool_choice?: VoiceRealtimeFunctionToolChoice
  // Mindtrip requires correlation metadata on every manual response.create,
  // including future out-of-band responses. mindtrip_trigger remains optional.
  metadata: VoiceResponseMetadata
  // Selects audio or text output for this response.
  output_modalities?: ["audio"] | ["text"]
}

export type VoiceTurnPersistenceStatus =
  | "persisting"
  | "persisted"
  | "persist_failed"

interface VoiceRealtimeResponse {
  id: string
  status?: string
  status_details?: unknown
  metadata?: VoiceResponseMetadata
  output?: VoiceRealtimeResponseOutputItem[]
  usage?: {
    input_tokens?: number
    output_tokens?: number
    input_token_details?: {
      cached_tokens?: number
      text_tokens?: number
      audio_tokens?: number
    }
  }
}

export type VoiceRealtimeEvent =
  | {
      type:
        | "conversation.item.created"
        | "conversation.item.added"
        | "conversation.item.done"
        | "conversation.item.retrieved"
      item: { id: string; type: string; role?: VoiceRealtimeMessageRole }
    }
  | {
      type: "conversation.item.input_audio_transcription.delta"
      item_id?: string
      delta: string
    }
  | {
      type: "conversation.item.input_audio_transcription.completed"
      item_id: string
      transcript: string
    }
  | {
      type: "conversation.item.input_audio_transcription.failed"
      item_id?: string
      error?: {
        message?: string
        code?: string
        type?: string
      }
    }
  | {
      type: "response.content_part.added"
      response_id: string
      item_id: string
      content_index: number
      part: { type: "audio" | "text" }
    }
  | {
      type: "response.output_audio_transcript.delta"
      response_id: string
      item_id: string
      delta: string
    }
  | {
      type: "response.output_audio_transcript.done"
      response_id: string
      item_id: string
      transcript: string
    }
  | {
      type: "input_audio_buffer.speech_started"
      item_id?: string
      audio_start_ms: number
    }
  | {
      type: "input_audio_buffer.speech_stopped"
      item_id: string
      audio_end_ms: number
    }
  | {
      type: "output_audio_buffer.started" | "output_audio_buffer.stopped"
      response_id: string
    }
  | { type: "output_audio_buffer.cleared"; response_id: string }
  | {
      type: "response.created"
      event_id?: string
      response: VoiceRealtimeResponse
    }
  | {
      type: "response.output_item.added"
      response_id: string
      item: { id: string; type: string; role?: VoiceRealtimeMessageRole }
    }
  | {
      type: "response.output_item.done"
      response_id: string
      item: { id: string; type: string; role?: VoiceRealtimeMessageRole }
    }
  | {
      type: "response.output_text.delta"
      response_id: string
      item_id: string
      output_index: number
      content_index: number
      delta: string
    }
  | {
      type: "response.output_text.done"
      response_id: string
      item_id: string
      output_index: number
      content_index: number
      text: string
    }
  | {
      type: "response.done"
      event_id?: string
      response?: VoiceRealtimeResponse
    }
  | {
      type: "response.function_call_arguments.done"
      response_id?: string
      item_id?: string
      output_index: number
      call_id: string
      name: string
      arguments: string
    }
  | {
      type: "conversation.item.truncated"
      item_id: string
      audio_end_ms: number
    }
  | {
      type: "error"
      event_id?: string
      response_id?: string
      message?: string
      code?: string
      error?: {
        message?: string
        code?: string
        type?: string
        event_id?: string
        param?: string | null
      }
    }
  | {
      type: "session.updated"
      session: { tools: VoiceRealtimeFunctionTool[] }
    }

export type VoiceRealtimeClientEvent =
  | { type: "output_audio_buffer.clear" }
  | {
      type: "conversation.item.retrieve"
      event_id: string
      item_id: string
    }
  | { type: "response.cancel"; response_id?: string }
  | {
      type: "session.update"
      session: {
        type: "realtime"
        audio: { output: { speed: number } }
      }
    }
  | {
      type: "session.update"
      session: {
        type: "realtime"
        audio: {
          input: {
            turn_detection: {
              type: "server_vad"
              // server_vad-only activation threshold from 0.0 to 1.0; defaults to 0.5.
              threshold?: number
              // server_vad-only audio to retain before speech detection; defaults to 300ms.
              prefix_padding_ms?: number
              // server_vad-only silence before a turn ends; defaults to 500ms.
              silence_duration_ms?: number
              // server_vad-only 5,000-30,000ms timeout after model audio playback ends.
              idle_timeout_ms?: number
              // Creates a response when server_vad detects speech has ended.
              create_response?: boolean
              // Cancels default-conversation output when server_vad detects new speech.
              interrupt_response?: boolean
            }
          }
        }
      }
    }
  | {
      type: "session.update"
      session: { type: "realtime"; tracing: VoiceRealtimeTracing }
    }
  | {
      type: "session.update"
      event_id: string
      session: { type: "realtime"; tools: VoiceRealtimeFunctionTool[] }
    }
  | {
      type: "conversation.item.create"
      event_id: string
      previous_item_id?: string
      item: {
        type: "function_call_output"
        id: string
        call_id: string
        output: string
      }
    }
  | {
      type: "conversation.item.create"
      event_id: string
      previous_item_id?: string
      item: {
        type: "function_call"
        id: string
        call_id: string
        name: string
        arguments: string
      }
    }
  | {
      type: "conversation.item.create"
      event_id: string
      previous_item_id?: string
      item: {
        type: "message"
        id: string
        role: "user"
        content: Array<{
          type: "input_image"
          image_url: string
          detail: "high"
        }>
      }
    }
  | {
      type: "conversation.item.create"
      event_id?: string
      previous_item_id: string
      item: VoiceHistoryItem & { id: string }
    }
  | {
      type: "conversation.item.create"
      item: {
        type: "message"
        // Present only for a marker the sender means to retract once it has
        // been acted on (conversation.item.delete needs an id to name).
        id?: string
        role: "system"
        content: [
          {
            type: "input_text"
            text: string
          },
        ]
      }
    }
  | { type: "conversation.item.delete"; item_id: string }
  | {
      type: "response.create"
      event_id: string
      response: VoiceRealtimeResponseCreateParams
    }

export interface VoiceFinalizedTranscriptTurn {
  role: VoiceTranscriptRole
  transcript: string
  // providerEventId is the persistence replacement identity; merged turns keep
  // every contributing provider item in providerEventIds for audit/persistence.
  providerEventId: string
  providerEventIds?: string[]
  toolCallIds?: string[]
  interrupted?: boolean
}

// One getStats reading of the two levels a voice visualization moves with.
// `null` is "no level to report", not silence: the browser may not meter a
// channel (inbound playback especially), and there is no transport at all
// before connect or after teardown. The distinction is load-bearing — a null
// assistant level is what sends the orb to its synthetic pulse, where zero
// would read as an assistant that has gone quiet.
export interface VoiceAudioLevelSample {
  mic: number | null
  assistant: number | null
}

// What a holder of a transport that may not exist yet reports without one.
export const UNMETERED_AUDIO_LEVELS: VoiceAudioLevelSample = {
  mic: null,
  assistant: null,
}

export type VoiceAudioDeviceKind = "audioinput" | "audiooutput"

export const VOICE_AUDIO_SYSTEM_DEVICE_ID = "__system_default__"

export const VOICE_OUTPUT_SPEED_PRESETS: readonly number[] = [
  0.8, 1.0, 1.25, 1.5,
]

export interface VoiceAudioDevice {
  deviceId: string
  label: string
  kind: VoiceAudioDeviceKind
}

export interface VoicePlaceAnnotationCandidate {
  originalText: string
  name: string
  type: string
  location?: string
  properNoun: boolean
  entityRef?: string
}

export interface VoicePlaceAnnotationCandidates {
  providerEventId: string
  messageId: number
  openaiCallId: string
  transcript: string
  toolCallIds?: string[]
  candidates: VoicePlaceAnnotationCandidate[]
}

export interface ToolOutputDelivery {
  callId: string
  toolName: string
  customerSpeechGeneration: number
  result: VoiceToolResult
  additionalToolCalls?: VoiceAdditionalToolCall[]
}

export interface PendingVoiceToolCallState {
  // The provider tool-call id remains the reconciliation key after reconnect.
  callId: string
  toolName: string
  handle: string
  chatId: number
  openaiCallId: string
  customerSpeechGeneration: number
  eventSeq?: number | null
  delivered: boolean
}

export interface VoiceSessionStateSnapshot {
  connectionStatus: VoiceConnectionStatus
  liveStream: VoiceLiveEntry[]
  // The measured caption pacing rate — the engine's pause cut uses it to back
  // the reveal's deliberate lead out of the cut position.
  captionSyllablesPerSecond: number
  activeAssistantResponseId: string | null
  audibleAssistantResponseId: string | null
  activeAssistantItemId: string | null
  selectedChatId: number | null
  selectedSpeed: number
  selectedOutputDeviceId: string
  speakerSelectionSupported: boolean
}

export interface StopVoiceSessionArgs {
  reconcileStoppedSession?: boolean
  // Keeps the microphone tracks live until this settles — see call-sounds.ts.
  holdCaptureUntil?: Promise<void>
}

export interface ReconcileRunningVoiceToolCall {
  status: string
  handle?: string | null
  callId: string
  openaiCallId: string
  toolName: string
}

export interface TranscriptAuditPayload {
  fullTranscript?: string
  truncatedAtMs?: number | null
  cutoffReason?: VoiceCutoffReason
}

export interface TranscriptPersistOptions {
  replaceExisting?: boolean
}

export interface PersistedUserItemForTool {
  itemId: string
  messageId: number
}

export interface VoiceSessionTeardown {
  stoppedChatId: number | null
  shouldReconcileStoppedSession: boolean
  // Settles after all queued transcript writes, including teardown's final
  // assistant-turn flush, have completed or failed. Callers reconcile only after
  // this boundary so the stopped session's final persistence result is visible.
  pendingPersists: Promise<void>
}

export interface VoiceChatRTCDeps {
  chatId: number
  getResponseInstructions?: (
    response: VoiceRealtimeResponseCreateParams
  ) => string | undefined
  // The manually-triggered initial response is voice-only by default. Bounded
  // flows that treat it as part of their durable transcript can opt in without
  // changing the normal voice-chat greeting lifecycle.
  persistInitialResponse?: boolean
  // Opt-in for bounded flows whose opening should play uninterrupted. Holds
  // the mic from connection through initial playback, not later replies.
  muteDuringInitialResponse?: boolean
  onPlaceAnnotationCandidates?: (
    payload: VoicePlaceAnnotationCandidates
  ) => Promise<void>
  reportPlaceAnnotationFailure?: (
    payload: ReportTranscriptAnnotationFailureArgs
  ) => Promise<void>
  getVoiceState: () => VoiceSessionStateSnapshot
  onCustomerSpeechStarted?: () => void
  onTurnTransition: (transition: VoiceTurnTransition) => void
  onChatRenamed?: (payload: { chatId: number; name: string }) => void
  onToolStarted: (payload: {
    callId: string
    toolName: string
    progressLabel: string
    query: string
    locationHint?: string
  }) => void
  onToolProgress: (payload: {
    callId: string
    statusMessage?: string
    estimatedCompletionPercent?: number
    complicationMessage?: string
    metricPayload?: AgentStatusMetricPayload
  }) => void
  onToolSucceeded: (payload: {
    callId: string
    candidates: VoiceChatToolCandidate[]
    hotelQuoteRefs: string[]
  }) => void
  onToolFailed: (payload: {
    callId: string
    toolName: string
    message: string
  }) => void
  onTurnPersisting: (payload: { providerEventId: string }) => void
  onTurnPersisted: (
    payload: {
      providerEventId: string
      interrupted?: boolean
    } & VoiceTranscriptPersistenceResult
  ) => void
  onTurnPersistFailed: (payload: VoiceTurnPersistFailure) => void
  onCaptionRateMeasured: (syllablesPerSecond: number) => void
  // Speech-gate transitions from the engine's playback-level sampling.
  // `speaking` is true/false while the browser meters the assistant's inbound
  // audio level, null when it doesn't (the pacer then runs ungated, Phase 1
  // behavior). `windowGated` is the engine's per-audio-window regime latch —
  // the single owner the scheduler follows (it can flip false→true within
  // the grace of a window's start). `assistantAudioStalled` is a presentation
  // signal latched after sustained measured silence and cleared by real speech.
  onAssistantSpeakingChanged: (payload: {
    speaking: boolean | null
    windowGated: boolean
    assistantAudioStalled: boolean
  }) => void
  // Fired when a transport that was fully connected becomes unusable. Startup
  // failures still reject connect(); intentional teardown never calls this.
  onConnectionLost?: (error: Error) => void
  onTransportReconnectingChanged?: (reconnecting: boolean) => void
  // A provider `error` event classified benign and swallowed (the session
  // keeps running). `code` is the provider's error code when it sent one.
  onBenignProviderError?: (code: string | null) => void
  // A barge-in that cancelled the assistant and then transcribed to nothing:
  // room noise or speaker echo the turn detector took for the customer.
  onPhantomBargeIn?: () => void
  // A sub-300ms barge-in that interrupted audible assistant audio.
  onShortBargeIn?: () => void
  // How long one window of assistant audio played, reported as the window
  // closes. Phantom barge-ins only mean something against this denominator.
  onAssistantAudioElapsed?: (elapsedMs: number) => void
  persistTranscript: (
    args: CreateTranscriptMessageArgs
  ) => Promise<VoiceTranscriptPersistenceResult>
  runTool: (args: RunToolArgs) => Promise<VoiceToolResult>
  runGraphqlTools: (
    args: RunGraphqlToolsArgs
  ) => Promise<VoiceGraphqlToolResult[]>
  fetchToolResult: (args: {
    chatId: number
    openaiCallId: string
    handle: string
  }) => Promise<VoiceToolResult>
  fetchToolMedia: (args: GetToolMediaArgs) => Promise<VoiceToolMedia[]>
  refreshToolCalls: (chatId: number) => Promise<unknown>
}

export interface VoiceTurnPersistFailure {
  providerEventId: string
  message: string
  rootProviderEventId?: string
  attemptCount?: number
  affectedTurnCount?: number
  openaiCallId?: string
}
